import {
  type TimeInputProps as BaseTimeInputProps,
  TimeInput as BaseTimeInput,
} from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import { alpha, styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { forwardRef, useRef, useState } from 'react';

import 'dayjs/locale/th';

import { AccessTimeIcon, CloseRoundedIcon } from '../../icons';
import { ColorPalette } from '../../theme';

function isNumber(value: number | null | undefined) {
  return value !== null && value !== undefined;
}

function valueToDate(
  value: number | undefined,
  unit: 'minutes' | 'seconds' | 'milliseconds',
): Date | undefined {
  // just in case if value is interrupted by external source
  if (!isNumber(value)) {
    return undefined;
  }

  let totalSeconds: number;

  switch (unit) {
    case 'minutes':
      totalSeconds = value * 60;
      break;
    case 'seconds':
      totalSeconds = value;
      break;
    case 'milliseconds':
      totalSeconds = value / 1000;
      break;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return dayjs().hour(hours).minute(minutes).second(seconds).millisecond(0).toDate();
}

type TimeValueInputProps = Omit<BaseTimeInputProps, 'sx' | 'onChange' | 'value'> & {
  helperText?: string;
  fullWidth?: boolean;
  onChange?: (value: number | undefined) => void;
  unit?: 'minutes' | 'seconds' | 'milliseconds';
  value?: number | undefined;
};

const StyledInputLabel = styled(InputLabel)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const StyledTimeValueInput = styled(BaseTimeInput, {
  shouldForwardProp: (prop) => prop !== 'color' && prop !== 'error',
})<
  Omit<TimeValueInputProps, 'sx' | 'onChange' | 'value'> & { color?: ColorPalette; error?: boolean }
>(({ theme, color: colorProp, error }) => {
  const color = (error ? ColorPalette.ERROR : colorProp) ?? ColorPalette.PRIMARY;
  return {
    marginTop: theme.spacing(6),
    border: '1px solid',
    borderColor: error ? theme.palette.error.main : theme.palette.grey['300'],
    borderRadius: theme.shape.rounded(1),
    'input, .mantine-Text-root': {
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      fontSize: 16,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    'input::placeholder, .mantine-Text-root': {
      color: theme.palette.text.disabled,
      fontSize: 18,
      textAlign: 'center',
    },
    ':has(input[value=""]) .mantine-Text-root': {
      color: `${theme.palette.text.disabled} !important`,
    },
    '.mantine-TimeInput-input': {
      paddingLeft: 10,
      border: 'none',
      minHeight: '42px',
      boxSizing: 'border-box',
      height: '1.4375em',
      ':hover': { borderColor: theme.palette[color].main },
      '&.mantine-TimeInput-disabled': {
        backgroundColor: '#F1F3F5',
        ':hover': { borderColor: theme.palette.grey['300'] },
        '&,input:disabled': {
          cursor: 'default',
          opacity: 1,
          color: theme.palette.text.disabled,
        },
      },
      ':has(input:focus)': {
        borderColor: theme.palette[color].main,
        boxShadow: `${alpha(theme.palette[color].main, 0.25)} 0 0 0 3px`,
      },
    },
  };
});

/**
 * This component is used to input time value and return the value in minutes, seconds or milliseconds
 *
 * @example

 *
 * <TimeValueInput
 *   label='เวลาเริ่มงาน'
 *   helperText='ระบุเวลาเริ่มงาน'
 *   color='primary'
 *   disabled={false}
 *   required={false}
 *   error={false}
 *   unit='seconds'
 * />

 *
 *
 */
const TimeValueInput = forwardRef((props: TimeValueInputProps, forwardedRef) => {
  const {
    label,
    value,
    onChange,
    helperText,
    disabled: isDisabled,
    error,
    color,
    clearable: isClearable = true,
    fullWidth: isFullWidth = true,
    required: isRequired,
    unit = 'minutes',
    ...restProps
  } = props;

  const timeInputRef = useRef<HTMLInputElement>(null);
  const ref = useMergedRef(timeInputRef, forwardedRef);
  const [time, setTime] = useState<Date | undefined>(valueToDate(value, unit));

  const handleTimeChange = (value: Date | undefined) => {
    setTime(value);
    if (!value) {
      onChange?.(undefined);
      return;
    }

    const time = dayjs(value);
    const totalSeconds = time.hour() * 3600 + time.minute() * 60 + time.second();

    const multipliers = {
      minutes: 1 / 60,
      seconds: 1,
      milliseconds: 1000,
    };

    const result = totalSeconds * multipliers[unit];
    onChange?.(result);
  };

  const handleClear = () => {
    setTime(undefined);
    onChange?.(undefined);
  };

  const rightButton =
    !isClearable || !time ? (
      <AccessTimeIcon
        sx={{
          color: !isDisabled && time ? 'text.secondary' : 'text.disabled',
          pointerEvents: 'none',
          fontSize: 24,
        }}
      />
    ) : (
      <IconButton onClick={handleClear} disabled={props.disabled} sx={{ color: 'text.secondary' }}>
        <CloseRoundedIcon />
      </IconButton>
    );

  return (
    <FormControl fullWidth={isFullWidth} required={isRequired}>
      {label ? <StyledInputLabel>{label}</StyledInputLabel> : null}
      <StyledTimeValueInput
        ref={ref}
        value={time}
        color={color as ColorPalette}
        size='md'
        rightSection={rightButton}
        rightSectionProps={{ style: { width: 0, right: 24 } }}
        onChange={handleTimeChange}
        error={!!error}
        disabled={isDisabled}
        {...restProps}
      />
      <FormHelperText error={!!error}>{helperText}</FormHelperText>
    </FormControl>
  );
});

TimeValueInput.displayName = 'TimeValueInput';

export { TimeValueInput };
export type { TimeValueInputProps };
